import moment from 'moment';
import _ from 'lodash';
import i18n from 'i18n';
import { toast } from 'react-toastify';
import { FireableUpdateEventListener } from 'utils/UpdateEventListener';
import { Order } from 'core/order/Order';
import { DraftCreateDTO, DraftValidationDTO } from 'core/draft/Draft';
import { DefaultDraftManager, DraftManager } from 'core/draft/DraftManager';
import { DefaultOrderManager, OrderManager } from 'core/order/OrderManager';

export class DefaultDraftSummaryStateContentModel {
  canGoNext = true;

  event: FireableUpdateEventListener<{ loading: boolean }>;

  summaryData: DraftCreateDTO[] = [];

  accountId = '';

  constructor(
    protected channelGroupId: number,
    private orderManager: OrderManager = new DefaultOrderManager(),
    private draftManager: DraftManager = new DefaultDraftManager()
  ) {
    this.event = new FireableUpdateEventListener<{ loading: boolean }>();
  }

  updateState(loading: boolean) {
    this.event.fireEvent({ loading });
  }

  setCanGoNext = (canGoNext: boolean) => {
    this.canGoNext = canGoNext;
  };

  setAccountId = (data: any) => {
    this.accountId = _.get(data, 'order.advertiserId', 0).toString();
  };

  getValidationData = async (
    data: any
  ): Promise<{
    [l1ObjectId: number]: {
      status: any;
    };
  }> => {
    const l2ObjectsToCreateDraft: any[] = _.get(
      data,
      'l2ObjectsToCreateDraft',
      []
    );
    const targets: any[] = _.get(data, 'targets', []);
    this.setAccountId(data);
    this.setSummaryData(l2ObjectsToCreateDraft, targets);
    const validationDataMap = await this.validate(targets);
    return validationDataMap;
  };

  setSummaryData = (l2ObjectsToCreateDraft: any[], targets: any[]) => {
    let summaryData: DraftCreateDTO[] = [];
    _.forEach(l2ObjectsToCreateDraft, (l2Object) => {
      _.forEach(targets, (target) => {
        let draftCreateDTO: DraftCreateDTO = {
          includeBinding: _.get(target, 'includeBinding', false),
          channelGroupId: this.channelGroupId,
          targetL1ObjectId: _.get(target, 'l1Object.value', 0),
          sourceL2ChannelId: _.get(l2Object, 'id', 0),
          numberOfCopies: _.get(target, 'numberOfCopies', 1),
          draft: _.get(l2Object, 'isDraft', false)
        };
        summaryData = [...summaryData, draftCreateDTO];
      });
    });
    this.summaryData = summaryData;
  };

  validateDelivery = (deliveryStatus: string) => {
    let status: any;
    switch (deliveryStatus) {
      case 'ACTIVE':
      case 'INACTIVE':
        break;
      default:
        status = {
          type: 'error',
          payload: i18n.t('draftSummaryStateContent.status.error.delivery')
        };
        break;
    }
    return _.omitBy(status, _.isNil);
  };

  validateBindings = (bindings: any[]) => {
    let status: any;
    const numOfInvalidBinding = _.filter(
      bindings,
      (binding) =>
        _.get(binding, 'reason', []).length > 0 || !binding.mappingGoCreative
    );
    if (numOfInvalidBinding.length > 0) {
      status = {
        type: 'warning',
        payload: i18n.t('draftSummaryStateContent.status.warning.bindings', {
          num: numOfInvalidBinding.length
        })
      };
    }
    return _.omitBy(status, _.isNil);
  };

  validateOrderEndDate = (targetOrder: Order) => {
    let status: any;
    const today = moment().startOf('day');
    const endDate = moment(targetOrder.endDate, 'YYYY-MM-DD');
    const diffEnd = endDate.diff(today, 'days');
    if (diffEnd < 0) {
      status = {
        type: 'error',
        payload: i18n.t('draftSummaryStateContent.status.error.endDate')
      };
    }
    return _.omitBy(status, _.isNil);
  };

  validateAccount = (channelAccountId: string) => {
    let status: any;
    if (this.accountId !== channelAccountId) {
      status = {
        type: 'error',
        payload: i18n.t('draftSummaryStateContent.status.error.account')
      };
    }
    return _.omitBy(status, _.isNil);
  };

  validate = async (targets: any[]) => {
    const targetOrderIds: number[] = _.map(targets, (target) =>
      _.get(target, 'order.value', 0)
    );

    const targetOrders: Order[] = await Promise.all(
      targetOrderIds.map((orderId) => this.orderManager.getOrderById(orderId))
    );
    const validationInfos: DraftValidationDTO[] =
      await this.draftManager.getValidationInfos(this.summaryData);

    let validationDataMap = {};
    _.forEach(targets, (target) => {
      const orderId = _.get(target, 'order.value', 0);
      const l1ObjectId = _.get(target, 'l1Object.value', 0);

      const targetOrder = _.filter(
        targetOrders,
        (order) => order.id === orderId
      )[0];
      const validationInfo = _.filter(
        validationInfos,
        (info) => info.targetL1ObjectId === l1ObjectId
      )[0];
      const statusList = _.filter(
        [
          this.validateOrderEndDate(targetOrder),
          this.validateDelivery(validationInfo.deliveryStatus),
          this.validateAccount(validationInfo.channelAccountId.toString()),
          this.validateBindings(validationInfo.bindings)
        ],
        (status) => !_.isEmpty(status)
      );
      validationDataMap = {
        ...validationDataMap,
        [l1ObjectId]: {
          status: !_.isEmpty(statusList)
            ? statusList
            : [
                {
                  type: 'passed',
                  payload: i18n.t('draftSummaryStateContent.status.passed')
                }
              ]
        }
      };
    });
    return validationDataMap;
  };

  save = async () => {
    try {
      this.updateState(true);
      await this.draftManager.createDraft(this.summaryData);
      toast.success(i18n.t('draftSummaryState.labels.createSuccess'));
      this.updateState(false);
    } catch (e) {
      e instanceof Error && toast.error(e.message);
      console.log('error', e);
      this.updateState(false);
    }
  };
}
